/**
 * useAdvancedUnlock — Checks if the user has met the requirements to unlock advanced mode.
 *
 * Requirements (all 5 must be met):
 * 1. At least 1 planning template exists
 * 2. At least 1 program exists
 * 3. At least 6 sessions exist
 * 4. At least 3 sessions linked to a program
 * 5. At least 1 active program
 *
 * @since Progressive Interface System
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface UnlockRequirement {
  id: string;
  label: string;
  met: boolean;
  current: number;
  target: number;
  /** Route to navigate for completing this requirement */
  actionRoute: string;
  actionLabel: string;
}

export interface AdvancedUnlockState {
  isUnlocked: boolean;
  requirements: UnlockRequirement[];
  completedCount: number;
  totalCount: number;
  loading: boolean;
}

export function useAdvancedUnlock(): AdvancedUnlockState {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["advanced-unlock", user?.id],
    queryFn: async (): Promise<UnlockRequirement[]> => {
      if (!user) return [];

      // First get user's program IDs (needed for linked sessions query)
      const { data: userPrograms } = await supabase
        .from("programs" as any)
        .select("id")
        .eq("user_id", user.id);
      const programIds = userPrograms?.map((p: any) => p.id) ?? [];

      // Now run all counts in parallel
      const [templates, programs, sessions, linkedSessions, intentProfile] = await Promise.all([
        supabase
          .from("planning_week_templates")
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
        supabase
          .from("programs" as any)
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
        supabase
          .from("sessions" as any)
          .select("id", { count: "exact", head: true })
          .eq("user_id", user.id),
        programIds.length > 0
          ? supabase
              .from("program_sessions")
              .select("id", { count: "exact", head: true })
              .in("program_id", programIds)
          : Promise.resolve({ count: 0 }),
        // A program is "active" when linked via current_program_id (planning link)
        // OR when its status field is 'active'
        // OR when it has active_week_types (linked to planning week types)
        supabase
          .from("user_intent_profiles")
          .select("current_program_id")
          .eq("user_id", user.id)
          .maybeSingle(),
      ]);

      const templateCount = templates.count ?? 0;
      const programCount = programs.count ?? 0;
      const sessionCount = sessions.count ?? 0;
      const linkedCount = linkedSessions.count ?? 0;
      // Active = linked to planning OR has status 'active' OR has active_week_types
      const hasLinkedProgram = !!(intentProfile.data as any)?.current_program_id;
      // Also check status field or active_week_types as fallback
      let hasStatusActive = false;
      if (!hasLinkedProgram) {
        const [statusCheck, weekTypesCheck] = await Promise.all([
          supabase
            .from("programs" as any)
            .select("id", { count: "exact", head: true })
            .eq("user_id", user.id)
            .eq("status", "active"),
          supabase
            .from("programs" as any)
            .select("id", { count: "exact", head: true })
            .eq("user_id", user.id)
            .not("active_week_types", "eq", "{}"),
        ]);
        hasStatusActive = ((statusCheck.count ?? 0) >= 1) || ((weekTypesCheck.count ?? 0) >= 1);
      }
      const activeCount = (hasLinkedProgram || hasStatusActive) ? 1 : 0;

      return [
        {
          id: "planning-template",
          label: "Créer un planning hebdomadaire",
          met: templateCount >= 1,
          current: Math.min(templateCount, 1),
          target: 1,
          actionRoute: "/planning",
          actionLabel: "Créer un planning",
        },
        {
          id: "program-created",
          label: "Créer un programme",
          met: programCount >= 1,
          current: Math.min(programCount, 1),
          target: 1,
          actionRoute: "/programs?tab=generate",
          actionLabel: "Créer un programme",
        },
        {
          id: "sessions-created",
          label: "Créer 6 séances",
          met: sessionCount >= 6,
          current: Math.min(sessionCount, 6),
          target: 6,
          actionRoute: "/sessions?tab=create",
          actionLabel: "Créer une séance",
        },
        {
          id: "sessions-linked",
          label: "Lier 3 séances à un programme",
          met: linkedCount >= 3,
          current: Math.min(linkedCount, 3),
          target: 3,
          actionRoute: "/programs",
          actionLabel: "Ouvrir mes programmes",
        },
        {
          id: "program-active",
          label: "Lier un programme au planning",
          met: activeCount >= 1,
          current: Math.min(activeCount, 1),
          target: 1,
          actionRoute: "/planning",
          actionLabel: "Ouvrir le planning",
        },
      ];
    },
    enabled: !!user,
    staleTime: 2 * 60_000,
  });

  const requirements = data ?? [];
  const completedCount = requirements.filter((r) => r.met).length;

  // Users with an active program unlock advanced immediately (core requirement).
  const hasActiveProgram = requirements.find((r) => r.id === "program-active")?.met ?? false;
  const allMet = completedCount === requirements.length && requirements.length > 0;

  return {
    isUnlocked: allMet || hasActiveProgram,
    requirements,
    completedCount,
    totalCount: requirements.length,
    loading: isLoading,
  };
}
